/**
 * PATH: packages/ui/src/components/sidebar/NavItemWithFlyout.tsx
 * FILE_ID: UI-SIDEBAR-NAVITEM-FLYOUT-001
 * PURPOSE: Nav item with flyout for children, collapsed tooltip, and expand/collapse
 * NOTES FOR FUTURE AI: Resolve last-hub path on click only. Keep item.href
 *      canonical for isActive — rewriting href to a deep last-route makes
 *      /schedule look inactive while the operator is on /schedule. Never OR
 *      NavLink's linkActive into the teal class: resume dest can equal the
 *      current URL for every sibling in a shared /workforce bucket.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useHubRouteMemory } from '../../hooks/useHubRouteMemory'
import { cn } from '../../lib/utils'
import type { NavItemWithFlyoutProps } from './types'
import { getBadgeClasses, getDisabledClasses, DisabledTooltipWrapper } from './sidebar-helpers'
import { navShortLabel } from './navShortLabel'

/** Collapsed rail tile — same visual language as tenant MobileDock. */
export const COLLAPSED_NAV_DOCK_TILE =
  'relative mx-auto flex w-[3.25rem] min-h-[52px] flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1.5 text-center transition-colors'

export function NavItemWithFlyout({ item, collapsed, accentBgClass, hubNavTargets, isExpanded = false, onToggleExpand, onContextMenu, onActivate }: NavItemWithFlyoutProps) {
  const [showFlyout, setShowFlyout] = useState(false)
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const flyoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const memoryTargets = useMemo(() => {
    if (hubNavTargets && hubNavTargets.length > 0) return hubNavTargets
    const hrefs: string[] = []
    if (item.href && !item.external && !/^https?:\/\//i.test(item.href)) hrefs.push(item.href)
    for (const child of item.children ?? []) {
      if (child.href && !/^https?:\/\//i.test(child.href)) hrefs.push(child.href)
    }
    return hrefs
  }, [hubNavTargets, item.href, item.external, item.children])
  const { resolveTarget } = useHubRouteMemory(memoryTargets)

  const hasChildren = item.children && item.children.length > 0
  const isDisabled = item.disabled === true
  const isActive = Boolean(item.href) && (
    location.pathname === item.href || location.pathname.startsWith(item.href + '/') ||
    (hasChildren && item.children!.some(child => location.pathname === child.href || location.pathname.startsWith(child.href + '/')))
  )

  const handleMouseEnter = useCallback(() => {
    if (flyoutTimeoutRef.current) clearTimeout(flyoutTimeoutRef.current)
    if (hasChildren && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const flyoutHeight = Math.min(320, (item.children?.length || 0) * 40 + 60)
      const viewportHeight = window.innerHeight
      const wouldOverflowBottom = rect.top + flyoutHeight > viewportHeight - 16
      setFlyoutPosition({ top: wouldOverflowBottom ? Math.max(16, viewportHeight - flyoutHeight - 16) : rect.top, left: rect.right + 8 })
      setShowFlyout(true)
    }
  }, [hasChildren, item.children?.length])

  const handleMouseLeave = useCallback(() => {
    flyoutTimeoutRef.current = setTimeout(() => setShowFlyout(false), 150)
  }, [])

  useEffect(() => { return () => { if (flyoutTimeoutRef.current) clearTimeout(flyoutTimeoutRef.current) } }, [])

  const forceNav = useCallback((href: string) => (e: React.MouseEvent) => {
    if (location.pathname === href || location.pathname.startsWith(href + '/')) { e.preventDefault(); navigate(href) }
  }, [location.pathname, navigate])

  // Flyout content (shared between collapsed and expanded non-expanded states)
  const flyoutPortal = hasChildren && showFlyout && !isDisabled && !(isExpanded && !collapsed) && createPortal(
    <div className="fixed w-52 bg-popover border border-border rounded-xl shadow-xl z-[9999] py-1.5 animate-in slide-in-from-left-2 duration-150 max-h-[calc(100vh-4rem)] overflow-y-auto"
      style={{ top: flyoutPosition.top, left: flyoutPosition.left }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {collapsed && <div className="px-3 pb-1.5 border-b border-border mb-1"><p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><item.icon className="w-3.5 h-3.5" />{item.label}</p></div>}
      <div className="max-h-64 overflow-y-auto">
        {item.children!.map((child) => {
          if (child.disabled) return (
            <DisabledTooltipWrapper key={child.id} item={child} collapsed={false}>
              <div className={cn('flex items-center gap-2.5 px-3 py-2 text-[13px] mx-1 rounded-md', getDisabledClasses(child, false), 'text-muted-foreground')}>
                <child.icon className="w-4 h-4 flex-shrink-0" /><span className="truncate flex-1">{child.label}</span>
                {child.premiumOnly && <span className="px-1 py-0.5 text-[9px] bg-amber-100 dark:bg-amber-900/40 text-amber-600 rounded">PRO</span>}
              </div>
            </DisabledTooltipWrapper>
          )
          const isChildActive = location.pathname === child.href
          return (
            <NavLink key={child.id} to={child.href ?? ''} end onClick={forceNav(child.href ?? '')}
              className={() => cn('flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors mx-1 rounded-md', isChildActive ? `${accentBgClass} text-white` : 'text-muted-foreground hover:bg-muted')}>
              <child.icon className="w-4 h-4 flex-shrink-0" /><span className="truncate flex-1">{child.label}</span>
              {child.badge !== undefined && <span className={cn('px-1.5 py-0.5 text-[10px] font-medium rounded-full', getBadgeClasses(child.badgeVariant))}>{child.badge}</span>}
            </NavLink>
          )
        })}
      </div>
    </div>,
    document.body
  )

  const shortLabel = navShortLabel(item.label)

  // Render link/button/div based on item type
  const renderItemLink = (extraClass: string) => {
    if (isDisabled) return (<div className={cn(extraClass, getDisabledClasses(item, collapsed), 'text-muted-foreground')} title={item.label}>
      <item.icon className={cn(collapsed ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0')} />
      {collapsed ? (
        <span className="max-w-full truncate text-[10px] font-medium leading-tight">{shortLabel}</span>
      ) : (
        <span className="truncate flex-1">{item.label}</span>
      )}
      {item.premiumOnly && collapsed && <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full flex items-center justify-center"><span className="text-[8px] text-white">★</span></span>}
      {!collapsed && item.premiumOnly && <span className="px-1 py-0.5 text-[9px] bg-amber-100 dark:bg-amber-900/40 text-amber-600 rounded font-medium">PRO</span>}
      {!collapsed && item.badge !== undefined && !hasChildren && !item.premiumOnly && <span className={cn('px-1.5 py-0.5 text-[10px] font-medium rounded-full opacity-50', getBadgeClasses(item.badgeVariant))}>{item.badge}</span>}
    </div>)
    if (item.action) return (<button onClick={() => { onActivate?.(); item.action?.() }} className={cn(extraClass, 'text-muted-foreground hover:bg-muted', !collapsed && 'text-left flex-1')}>
      <item.icon className={cn(collapsed ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0')} />
      {collapsed ? (
        <span className="max-w-full truncate text-[10px] font-medium leading-tight">{shortLabel}</span>
      ) : (
        <>
          <span className="truncate flex-1">{item.label}</span>
          {item.badge !== undefined && !hasChildren && (
            <span className={cn('px-1.5 py-0.5 text-[10px] font-medium rounded-full', getBadgeClasses(item.badgeVariant))}>{item.badge}</span>
          )}
        </>
      )}
    </button>)
    if (!item.href) return null
    // Cross-domain / SSO link: render as a plain anchor so the browser
    // performs a full navigation. NavLink would treat absolute URLs as
    // relative paths and never leave the SPA.
    const isAbsoluteHref = /^https?:\/\//i.test(item.href)
    if (item.external || isAbsoluteHref) {
      return (<a href={item.href}
        onClick={() => onActivate?.()}
        className={cn(extraClass, 'text-muted-foreground hover:bg-muted')}>
        <item.icon className={cn(collapsed ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0')} />
        {collapsed ? (
          <span className="max-w-full truncate text-[10px] font-medium leading-tight">{shortLabel}</span>
        ) : (
          <>
            <span className="truncate flex-1">{item.label}</span>
            {item.badge !== undefined && !hasChildren && (
              <span className={cn('px-1.5 py-0.5 text-[10px] font-medium rounded-full', getBadgeClasses(item.badgeVariant))}>{item.badge}</span>
            )}
          </>
        )}
      </a>)
    }
    // `href` is a narrowed const (item.href is non-undefined after the
    // guard above) — capturing it keeps the onClick closure's
    // `forceNav(href)` typed as `string` (a property access inside a new
    // closure would otherwise re-widen to `string | undefined`).
    const href = item.href
    const dest = resolveTarget(href)
    return (<NavLink to={dest} end={href === '/' || href === '/dashboard' || href.endsWith('/')} onClick={(e) => { onActivate?.(); forceNav(dest)(e) }}
      className={() => cn(extraClass,
        collapsed ? (isActive ? `${accentBgClass} text-white` : 'text-muted-foreground hover:bg-muted')
        : (isActive && !hasChildren) ? `${accentBgClass} text-white` : isActive && hasChildren ? 'text-foreground bg-muted' : 'text-muted-foreground hover:bg-muted')}>
      <item.icon className={cn(collapsed ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0')} />
      {collapsed ? (
        <span className="max-w-full truncate text-[10px] font-medium leading-tight">{shortLabel}</span>
      ) : (
        <>
          <span className="truncate flex-1">{item.label}</span>
          {item.badge !== undefined && !hasChildren && (
            <span className={cn('px-1.5 py-0.5 text-[10px] font-medium rounded-full', getBadgeClasses(item.badgeVariant))}>{item.badge}</span>
          )}
        </>
      )}
    </NavLink>)
  }

  const baseClass = collapsed
    ? COLLAPSED_NAV_DOCK_TILE
    : 'flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors text-[13px] flex-1'

  const content = (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={!isDisabled ? handleMouseEnter : undefined}
      onMouseLeave={!isDisabled ? handleMouseLeave : undefined}
      // Right-click → open the Sidebar's shared context menu. Wired
      // only when the host passed a `resolveNavContext` prop —
      // otherwise we leave the browser's native context menu alone so
      // existing hubs that haven't opted in see ZERO behavioural
      // change. Disabled items also fall through to the native menu
      // so users can still e.g. inspect the rendered DOM.
      onContextMenu={onContextMenu && !isDisabled ? onContextMenu : undefined}
    >
      <div className="flex items-center">
        {renderItemLink(baseClass)}
        {!collapsed && hasChildren && !isDisabled && (
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleExpand?.() }} className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors">
            <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', isExpanded && 'rotate-90')} />
          </button>
        )}
      </div>
      {/* Inline expanded children */}
      {!collapsed && hasChildren && isExpanded && !isDisabled && (
        <ul className="mt-0.5 ml-4 pl-2 border-l border-border space-y-0.5">
          {item.children!.map((child) => child.disabled ? (
            <li key={child.id}><DisabledTooltipWrapper item={child} collapsed={false}>
              <div className={cn('flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px]', getDisabledClasses(child, false), 'text-muted-foreground')}>
                <child.icon className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate flex-1">{child.label}</span>
                {child.premiumOnly && <span className="px-1 py-0.5 text-[8px] bg-amber-100 dark:bg-amber-900/40 text-amber-600 rounded font-medium">PRO</span>}
              </div>
            </DisabledTooltipWrapper></li>
          ) : (
            <li key={child.id}><NavLink to={child.href ?? ''} end onClick={forceNav(child.href ?? '')}
              className={() => cn('flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-[12px]', location.pathname === child.href ? `${accentBgClass} text-white` : 'text-muted-foreground hover:bg-muted')}>
              <child.icon className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate flex-1">{child.label}</span>
              {child.badge !== undefined && <span className={cn('px-1 py-0.5 text-[9px] font-medium rounded-full', getBadgeClasses(child.badgeVariant))}>{child.badge}</span>}
            </NavLink></li>
          ))}
        </ul>
      )}
      {flyoutPortal}
    </div>
  )

  // Tooltip wrapping
  if (isDisabled) return <DisabledTooltipWrapper item={item} collapsed={collapsed}>{content}</DisabledTooltipWrapper>
  // Collapsed dock tiles show a short label under the icon (MobileDock
  // parity) — tooltips are only for disabled rows and parents w/ flyout.
  if (collapsed && !hasChildren && !isDisabled) {
    return content
  }
  return <DisabledTooltipWrapper item={item} collapsed={collapsed}>{content}</DisabledTooltipWrapper>
}
